import React, { useContext, useMemo, useState } from 'react';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { DataContext } from '../../components/DataContext';
import '../../styles/components/_gamesPerformanceTable.css';

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '...';

  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
};

const formatReturn = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '...';

  return `${amount >= 0 ? '+' : ''}${amount.toFixed(0)}%`;
};

const performanceColumns = [
  { key: 'playerName', label: 'Player', type: 'text' },
  { key: 'gameNumber', label: 'Game #' },
  { key: 'totalMoneyIn', label: 'In' },
  { key: 'totalMoneyOut', label: 'Out' },
  { key: 'winnings', label: 'Winnings' },
  { key: 'return', label: 'Return' },
];

// Precedence when a row qualifies for more than one marker (e.g. a 2-player
// game where the loser is also technically "second place"): winner beats
// loser beats second beats third, so a row only ever shows one emoji.
const getPlacementMarker = (row) => {
  if (row.isWinner) return { emoji: '🥇', label: 'gold medal' };
  if (row.isLoser) return { emoji: '💩', label: 'loser' };
  if (row.isSecond) return { emoji: '🥈', label: 'silver medal' };
  if (row.isThird) return { emoji: '🥉', label: 'bronze medal' };
  return null;
};

export default function GamesPerformanceTable() {
  const { gamesData } = useContext(DataContext);
  const [sortKey, setSortKey] = useState('winnings');
  const [sortDirection, setSortDirection] = useState('desc');

  const performanceRows = useMemo(() => {
    if (!gamesData || typeof gamesData !== 'object') return [];

    const games = Object.values(gamesData);
    const latestGameNumber = Math.max(...games.map((game) => Number(game.gameNumber)));
    const biggestPot = Math.max(...games.map((game) => Number(game.moneyOnTable)));

    return games
      .flatMap((game) => {
        const players = [...(game.playerDetails || [])]
          .sort((a, b) => Number(b.winnings) - Number(a.winnings));

        return players.map((player, index) => ({
          ...player,
          gameNumber: game.gameNumber,
          isWinner: index === 0,
          isSecond: index === 1,
          isThird: index === 2,
          isLoser: index === players.length - 1,
          isLatest: Number(game.gameNumber) === latestGameNumber,
          isBiggestPot: Number(game.moneyOnTable) === biggestPot,
        }));
      });
  }, [gamesData]);

  const sortedRows = useMemo(() => {
    const directionMultiplier = sortDirection === 'asc' ? 1 : -1;

    return [...performanceRows].sort((rowA, rowB) => {
      const column = performanceColumns.find(({ key }) => key === sortKey);
      const valueA = column?.type === 'text'
        ? String(rowA[sortKey]).toLowerCase()
        : Number(rowA[sortKey]);
      const valueB = column?.type === 'text'
        ? String(rowB[sortKey]).toLowerCase()
        : Number(rowB[sortKey]);
      const comparison = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;

      return comparison * directionMultiplier;
    });
  }, [performanceRows, sortDirection, sortKey]);

  const handleSort = (nextSortKey) => {
    if (nextSortKey === sortKey) {
      setSortDirection((currentDirection) => currentDirection === 'asc' ? 'desc' : 'asc');
      return;
    }

    setSortKey(nextSortKey);
    setSortDirection('asc');
  };

  const getSortIcon = (columnKey) => {
    if (columnKey !== sortKey) return null;
    return sortDirection === 'asc'
      ? <ArrowUpward className="performance-sort-icon" aria-hidden="true" />
      : <ArrowDownward className="performance-sort-icon" aria-hidden="true" />;
  };

  return (
    <section className="games-performance" aria-label="Individual game performances">
      <div className="games-performance-meta">
        <span>{performanceRows.length} Records</span>
      </div>
      <div className="games-performance-scroll">
        <table className="games-performance-table">
          <thead>
            <tr>
              {performanceColumns.map((column) => (
                <th
                  aria-sort={column.key === sortKey ? `${sortDirection}ending` : 'none'}
                  key={column.key}
                >
                  <button
                    className={`performance-sort-button${column.key === sortKey ? ' performance-sort-button-active' : ''}`}
                    type="button"
                    onClick={() => handleSort(column.key)}
                  >
                    <span>{column.label}</span>
                    {getSortIcon(column.key)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody key={`${sortKey}-${sortDirection}`}>
            {sortedRows.map((row, index) => {
              const marker = getPlacementMarker(row);

              return (
                <tr
                  className={`${row.isWinner ? 'performance-row-winner' : ''}${row.isLoser ? ' performance-row-loser' : ''}${row.isLatest ? ' performance-row-latest' : ''}${row.isBiggestPot ? ' performance-row-high-roller' : ''}`}
                  key={`${row.gameNumber}-${row.playerName}-${index}`}
                  style={{ '--row-i': index }}
                >
                  <td>
                    <span className="performance-player">
                      {marker && (
                        <span className="performance-mark" role="img" aria-label={marker.label}>
                          {marker.emoji}
                        </span>
                      )}
                      <span>{row.playerName}</span>
                    </span>
                  </td>
                  <td>
                    <span className="performance-game-number">{row.gameNumber}</span>
                  </td>
                  <td>{formatCurrency(row.totalMoneyIn)}</td>
                  <td>{formatCurrency(row.totalMoneyOut)}</td>
                  <td className={Number(row.winnings) >= 0 ? 'performance-positive' : 'performance-negative'}>
                    {Number(row.winnings) >= 0 ? '+' : ''}{formatCurrency(row.winnings)}
                  </td>
                  <td className={Number(row.return) >= 0 ? 'performance-positive' : 'performance-negative'}>
                    {formatReturn(row.return)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!performanceRows.length && <p className="games-performance-empty">Loading performances...</p>}
    </section>
  );
}